import React from 'react';
import { MdReceiptLong } from 'react-icons/md';
import Modal from '../Common/Modal';
import { claimRejectionNote, claimStatusLabel, flattenFields, formatDate, isClaimRejectionLabel } from '../../Utils/stediUtils';
import { Detail, FixSteps, Kicker, MUTED, PANEL_STYLE, ResultBanner, TITLE, TONES } from './StediResultParts';
import { ServiceLineFields } from './StediViewParts';

const LABEL_TONES = {
  Finalized: 'success',
  Denied: 'danger',
  Error: 'danger',
  Rejected: 'warning',
  'Payer Unavailable': 'warning',
  Pending: 'warning',
  Acknowledged: 'warning',
  'Info Requested': 'warning',
  'Not Found': 'neutral',
};

const LABEL_DETAILS = {
  Finalized: 'The payer has finished processing this claim.',
  Denied: 'The payer finalized this claim as denied.',
  Pending: 'The payer is still processing this claim.',
  Acknowledged: 'The payer has received the claim but not started processing it.',
  'Info Requested': 'The payer needs more information before it can process this claim.',
  Error: 'The payer could not process the status request.',
  'Payer Unavailable': "The payer's system did not respond in time. Nothing needs correcting; check again later.",
  'Not Found': 'The payer found no claim for this patient and these dates of service.',
};

// Shown in each claim's header, so left out of its field cards.
const HEADER_KEYS = ['statusCategoryCode', 'statusCategoryCodeValue', 'statusCode', 'statusCodeValue', 'tradingPartnerClaimNumber'];

function ClaimCard({ claim, index }) {
  const status = claim.claimStatus ?? {};
  const tone = /^F2/.test(status.statusCategoryCode ?? '') ? 'danger'
    : /^F/.test(status.statusCategoryCode ?? '') ? 'success'
      : /^(E|R)/.test(status.statusCategoryCode ?? '') ? 'danger' : 'warning';
  const { bg, text } = TONES[tone];
  const fields = flattenFields(Object.fromEntries(Object.entries(status).filter(([key]) => !HEADER_KEYS.includes(key))));

  return (
    <div className="rounded-4 border p-3" style={{ borderColor: 'var(--hc-surface-border)' }}>
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div style={{ minWidth: 0 }}>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ background: bg, color: text }}>
              {status.statusCategoryCode || `Claim ${index + 1}`}
            </span>
            <span className="fw-bold" style={TITLE}>{status.statusCategoryCodeValue || 'Claim status'}</span>
          </div>
          {status.statusCodeValue && <div className="small" style={MUTED}>{status.statusCodeValue}</div>}
        </div>
        {status.tradingPartnerClaimNumber && (
          <div className="text-sm-end">
            <div className="small" style={MUTED}>Payer Claim #</div>
            <div className="fw-bold" style={TITLE}>{status.tradingPartnerClaimNumber}</div>
          </div>
        )}
      </div>
      {fields.length > 0 && (
        <div className="row g-2">
          {fields.map(([label, value]) => (
            <div className="col-6 col-md-4 col-xl-3" key={label}>
              <div className="h-100 rounded-3 border px-3 py-2" style={PANEL_STYLE}>
                <div className="small" style={MUTED}>{label}</div>
                <div className="small fw-semibold text-break" style={TITLE}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Result of one real-time claim status check, shown before opening the saved record.
// `result` is what POST /stedi/claim-status returns: the stored summary plus Stedi's `response`.
export default function ClaimStatusResultModal({ result, payerName, payerId, onClose }) {
  if (!result) return null;

  const response = result.response ?? {};
  const claims = (Array.isArray(response.claims) ? response.claims : []).filter((claim) => claim && typeof claim === 'object');
  const errors = Array.isArray(response.errorResponse?.errors) ? response.errorResponse.errors : [];
  const label = claimStatusLabel(result);
  const tone = LABEL_TONES[label] ?? (isClaimRejectionLabel(label) ? 'danger' : 'neutral');
  const payerUnavailable = label === 'Payer Unavailable';
  // The payer's own reason; for a rejection that names no field, also where to find which one.
  let detail = [claims[0]?.claimStatus?.statusCodeValue, claimRejectionNote(claims[0]?.claimStatus)].filter(Boolean).join(' ')
    || LABEL_DETAILS[label];
  if (payerUnavailable) detail = LABEL_DETAILS[label];
  else if (result.payerRejected) detail = errors[0]?.description || 'The payer rejected the claim status request.';

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      scrollable
      icon={<MdReceiptLong size={22} />}
      title="Claim Status Result"
      subtitle={[result.patientName, result.memberId && `Member ${result.memberId}`].filter(Boolean).join(' · ')}
      closeOnBackdrop={false}
    >
      <div className="d-flex flex-column gap-4">
        <ResultBanner tone={tone} label={label} detail={detail} payerName={payerName} payerId={payerId} />

        {/* Stedi's "Please Correct and Resubmit" steps don't apply when the payer simply did not answer. */}
        {result.payerRejected && !payerUnavailable && errors.length > 0 && <FixSteps errors={errors} tone={tone} />}

        {result.procedureCode && claims.length > 0 && (
          <section>
            <Kicker>{`Service line ${result.procedureCode}`}</Kicker>
            <ServiceLineFields
              response={response}
              procedureCode={result.procedureCode}
              chargeAmount={result.lineChargeAmount}
              colClass="col-6 col-md-4"
            />
          </section>
        )}

        {claims.length > 0 && (
          <section>
            <Kicker>{`Claims (${claims.length})`}</Kicker>
            <div className="d-flex flex-column gap-3">
              {claims.map((claim, index) => <ClaimCard key={index} claim={claim} index={index} />)}
            </div>
          </section>
        )}

        <section>
          <Kicker>Check details</Kicker>
          <div className="rounded-4 border p-3" style={PANEL_STYLE}>
            <div className="row g-3">
              <Detail label="Patient" value={result.patientName} />
              <Detail label="Date of Birth" value={result.dateOfBirth && formatDate(result.dateOfBirth)} />
              <Detail label="Member ID" value={result.memberId} />
              <Detail label="Payer Claim #" value={result.payerClaimNumber} />
              <Detail label="Claims Returned" value={String(result.claimCount ?? claims.length)} />
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
}
